import { getCoinInfo } from '../../api'
import { percentageBetweenRange, truncateMiddle } from '../helpers'

import coinsStore from '../coins-store'
import { createSelector } from 'reselect'

export const COINS_FETCHING = 'COINS_FETCHING'
export const COINS_FETCHED = 'COINS_FETCHED'

export const fetchCoinInfo = id => (dispatch, getState) => {
  // dispatch({ type: COINS_FETCHING })

  return getCoinInfo(id).then(({ data }) => {
    dispatch({
      id,
      type: COINS_FETCHED,
      data: normalize(data, coinsStore.coins[id])
    })
  })
}

const initialState = {
  map: {}
}

export default function reducer(state = initialState, action) {

  switch (action.type) {
    case COINS_FETCHING: {
      return state
    }

    case COINS_FETCHED: {
      const map = {
        ...state.map,
        [action.id]: action.data
      }

      return { ...state, map }
    }

    default:
      return state
  }
}

export const selectCoin = createSelector(
  state => state.coinInfo.map,
  state => state.markets.coins,
  state => state.defi.coins,
  (_, coin) => coin,
  (map, coins, defi, coinId) => {
    let coin = map[coinId]
    if (coin) {
      return coin
    }

    coin = coins.find(item => item.id === coinId)

    if (!coin) {
      coin = defi.find(item => item.id === coinId)
    }

    return {
      ...coin,
      fetching: true,
      markets: [],
      performance: [],
      priceRanges: [],
      volumes: {}
    }
  }
)

// Helpers / Normalizers

function normalize(coin, storedCoin) {
  const marketData = coin.market_data
  const mergedCoin = {
    id: coin.id,
    name: coin.name,
    symbol: coin.symbol,
    image: coin.image.large,
    rank: coin.coingecko_rank,
    description: coin.description.en,
    price: marketData.current_price.usd,
    platforms: coin.platforms,
    priceChange24: marketData.price_change_percentage_24h,

    links: normalizeLinks(coin.links),
    markets: normalizeMarkets(coin.tickers),
    performance: normalizePerformance(coin.market_data),
    priceRanges: normalizePriceRange(coin.market_data),
    volumes: normalizeVolumes(coin.market_data),
    launchDate: coin.genesis_date
  }

  return mergeStoredData(mergedCoin, storedCoin)
}

function mergeStoredData(coin, storedCoin) {
  if (!storedCoin) {
    return coin
  }

  // Merge only existing links
  Object.keys(storedCoin.links).forEach(key => {
    const link = storedCoin.links[key]
    if (link) {
      coin.links[key] = link
    }
  })

  coin.categories = storedCoin.categories
  coin.guide = storedCoin.guide
  coin.whitepaper = storedCoin.whitepaper
  coin.funds = storedCoin.funds.map(fund => coinsStore.funds[fund])

  if (storedCoin.description) {
    coin.description = storedCoin.description
  }

  return coin
}

function normalizeLinks(links) {
  const map = {}
  
  // Handle our API format (simple object with direct links)
  if (links.reddit) {
    map.reddit = links.reddit
  }
  
  if (links.telegram) {
    map.telegram = links.telegram.startsWith('http') ? links.telegram : `https://t.me/${links.telegram}`
  }
  
  if (links.twitter) {
    map.twitter = links.twitter.startsWith('http') ? links.twitter : `https://twitter.com/${links.twitter}`
  }
  
  if (links.website) {
    map.website = Array.isArray(links.website) ? links.website[0] : links.website
  }
  
  // Legacy CoinGecko format support
  if (links.subreddit_url) {
    map.reddit = links.subreddit_url
  }

  if (links.telegram_channel_identifier) {
    map.telegram = `https://t.me/${links.telegram_channel_identifier}`
  }

  if (links.twitter_screen_name) {
    map.twitter = `https://twitter.com/${links.twitter_screen_name}`
  }

  if (links.homepage && links.homepage.length) {
    map.website = links.homepage[0]
  }

  return map
}
function normalizeMarkets(tickers) {
  return tickers.map(
    item => {
      return ({
        name: item.market.name,
        pair: `${truncateMiddle(item.base, 15)}/${truncateMiddle(item.target, 15)}`,
        price: item.converted_last.usd,
        volume: item.converted_volume.usd
      })
    }
  ).sort((a, b) =>
    b.volume - a.volume
  )
}

function normalizeVolumes(marketData) {
  return {
    totalVolume: parseFloat(marketData.total_volume?.usd || marketData.total_volume || 0),
    totalSupply: parseFloat(marketData.total_supply || 0),
    circulatingSupply: parseFloat(marketData.circulating_supply || 0),
    marketCap: parseFloat(marketData.market_cap?.usd || marketData.market_cap || 0),
    dilutedValuation: parseFloat(marketData.fully_diluted_valuation?.usd || marketData.fully_diluted_valuation || 0),
    tvl: marketData.total_value_locked ? parseFloat(marketData.total_value_locked?.usd || marketData.total_value_locked) : null
  }
}
function normalizePerformance(marketData) {
  const performance = []
  const performanceCoins = ['usd', 'btc', 'eth', 'bnb']
  const performancePeriods = [
    { name: '1w', key: 'price_change_percentage_7d_in_currency' },
    { name: '1m', key: 'price_change_percentage_30d_in_currency' }
  ]

  performanceCoins.forEach(code => {
    const perf = { code }
    performancePeriods.forEach(period => {
      const data = marketData[period.key]
      if (data && data[code]) {
        perf[period.name] = data[code]
      }
    })

    performance.push(perf)
  })

  return performance
}

function normalizePriceRange(marketData) {
  const range24h = {
    type: '24h Range',
    min: marketData.low_24h.usd,
    max: marketData.high_24h.usd
  }

  const range7d = {
    type: '7d Range',
    min: Math.min(...marketData.sparkline_7d.price),
    max: Math.max(...marketData.sparkline_7d.price)
  }

  const rangeAll = {
    type: 'All Time',
    min: marketData.atl.usd,
    max: marketData.ath.usd
  }

  return [range24h, range7d, rangeAll].map(item => {
    item.value = percentageBetweenRange(marketData.current_price.usd, item.min, item.max)
    return item
  })
}
